import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { AuthError } from '@supabase/supabase-js';
import Modal from '../Modal';
import { validateNewPassword, PASSWORD_HELP_TEXT } from '../../utils/passwordValidation';
import './change-password-modal.scss';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const supabase = useSupabaseClient();
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // Don't let a pending auto-close fire after unmount or a manual close
  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  const resetAndClose = useCallback(() => {
    clearCloseTimer();
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
    setLoading(false);
    onClose();
  }, [onClose, clearCloseTimer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateNewPassword(password, confirmPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        throw updateError;
      }

      setSuccess(true);
      closeTimerRef.current = setTimeout(resetAndClose, 2000);
    } catch (err) {
      const authError = err as AuthError;
      console.error('Password change error:', authError);
      setError(authError.message || 'Failed to update your password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={resetAndClose} title="Change Password" size="sm">
      <div className="change-password-modal">
        {success ? (
          <div className="change-password-modal__success">
            <div className="change-password-modal__success-icon">✓</div>
            <p>Your password has been updated.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="new-password">New Password</label>
              <input
                type="password"
                id="new-password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(null); }}
                placeholder="Enter your new password"
                disabled={loading}
                autoComplete="new-password"
              />
              <small className="change-password-modal__help">{PASSWORD_HELP_TEXT}</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirm-new-password">Confirm New Password</label>
              <input
                type="password"
                id="confirm-new-password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setError(null); }}
                placeholder="Confirm your new password"
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="change-password-modal__actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetAndClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
